import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, dateFmt, errMsg, LEAD_STAGES, STAGE_LABEL, LEAD_CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import StatusBadge from "@/components/StatusBadge";
import AccountsPanel from "@/components/AccountsPanel";
import SalesReport from "@/components/SalesReport";
import { Target, Phone, EnvelopeSimple, MapPin, Trophy, ListChecks, MagnifyingGlass, BellRinging, PhoneCall, HandGrabbing } from "@phosphor-icons/react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="p-6 bg-white" data-testid={`sales-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon size={26} weight="duotone" color={accent || "#002FA7"} />
      <div className="font-display font-black text-3xl text-[#111827] mt-3">{value}</div>
      <div className="text-sm text-[#4B5563] mt-1">{label}</div>
    </div>
  );
}

export default function SalesDashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: leads = [] } = useQuery({ queryKey: ["leads", "sales"], queryFn: () => api.leads() });
  const { data: stats } = useQuery({ queryKey: ["leadStats"], queryFn: api.leadStats });
  const { data: upcoming } = useQuery({ queryKey: ["upcomingReminders"], queryFn: api.upcomingReminders });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [view, setView] = useState("leads");
  const SALES_TABS = [["leads", "My Leads"], ["accounts", "Accounts"], ["activity", "My Activity"]];

  const frontDesk = leads.filter((l) => l.source === "Front Desk" && !l.assigned_to);
  const claim = useMutation({
    mutationFn: (id) => api.claimLead(id),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Claimed — it's now in your pipeline"); },
    onError: (e) => toast.error(errMsg(e, "Could not claim")),
  });

  const active = leads.filter((l) => !["won", "lost"].includes(l.status));
  const filtered = leads.filter((l) => {
    if (cat !== "all" && l.category !== cat) return false;
    if (q) {
      const t = `${l.company_name} ${l.industry} ${l.email} ${l.phone} ${l.city}`.toLowerCase();
      if (!t.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline text-[#002FA7]">Sales workspace</span>
          <h1 className="font-display font-black text-4xl text-[#111827] mt-2">Hi {user.name}, here are your leads.</h1>
          <p className="text-[#4B5563] mt-2">{user.company_name} · {user.title || "Sales Manager"}</p>
        </div>
        <Link to="/prospect" data-testid="sales-prospect-cta"
          className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98]">
          <MagnifyingGlass size={18} weight="bold" /> Find prospects
        </Link>
      </div>

      <div className="flex gap-1 border-b border-[#E5E7EB] mt-8" data-testid="sales-tabs">
        {SALES_TABS.map(([id, label]) => (
          <button key={id} data-testid={`sales-tab-${id}`} onClick={() => setView(id)}
            className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 transition-colors ${view === id ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#4B5563] hover:text-[#111827]"}`}>{label}</button>
        ))}
      </div>

      {view === "accounts" && <div className="mt-6"><AccountsPanel /></div>}
      {view === "activity" && <div className="mt-6"><SalesReport /></div>}

      {view === "leads" && (<>
      <div className="grid grid-cols-2 lg:grid-cols-4 grid-skeleton border-l border-t border-[#E5E7EB] mt-8">
        <Stat icon={ListChecks} label="Assigned Leads" value={stats?.total ?? 0} />
        <Stat icon={Target} label="Active" value={active.length} accent="#D97706" />
        <Stat icon={Trophy} label="Won" value={stats?.won ?? 0} accent="#047857" />
        <Stat icon={Target} label="Qualified+" value={(stats?.by_stage?.qualified ?? 0) + (stats?.by_stage?.proposal ?? 0)} />
      </div>

      {/* Front desk enquiries — shared inbox, anyone can claim */}
      {frontDesk.length > 0 && (
        <div className="border-2 border-[#0891B2] bg-[#ECFEFF] p-5 mt-6" data-testid="fd-inbox">
          <div className="flex items-center gap-2">
            <PhoneCall size={18} weight="fill" color="#0891B2" />
            <span className="font-semibold text-[#0E7490]">New guest enquiries from Front Desk — grab one to work it</span>
          </div>
          <div className="mt-3 space-y-2">
            {frontDesk.map((l) => (
              <div key={l.id} className="flex items-center justify-between bg-white border border-[#A5F3FC] px-4 py-3" data-testid={`fd-lead-${l.id}`}>
                <div>
                  <div className="font-display font-bold text-[#111827]">{l.guest_name || l.company_name} <span className="text-xs bg-[#CFFAFE] text-[#0E7490] px-2 py-0.5 font-semibold ml-1">Front Desk</span></div>
                  <div className="text-sm text-[#4B5563]">{l.hotel_name} · {l.phone}{l.check_in ? ` · ${dateFmt(l.check_in)}–${dateFmt(l.check_out)}` : ""} · {l.rooms_requested || 1} rooms</div>
                  {l.notes && <div className="text-xs text-[#9CA3AF] mt-0.5">{l.notes}</div>}
                </div>
                <button data-testid={`claim-lead-${l.id}`} disabled={claim.isPending} onClick={() => claim.mutate(l.id)}
                  className="bg-[#0891B2] text-white px-4 py-2 text-sm font-semibold hover:bg-[#0E7490] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center gap-1 whitespace-nowrap">
                  <HandGrabbing size={15} weight="bold" /> Claim
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {upcoming?.count > 0 && (
        <div className="border border-[#111827] p-5 mt-6" data-testid="sales-upcoming-reminders">
          <div className="flex items-center gap-2">
            <BellRinging size={18} weight="bold" color="#B45309" />
            <span className="font-semibold text-[#111827]">Upcoming reminders</span>
          </div>
          <div className="mt-3 space-y-2">
            {upcoming.reminders.slice(0, 6).map((r) => (
              <Link key={r.reminder_id} to={`/leads/${r.lead_id}`}
                className="flex items-center justify-between border border-[#E5E7EB] px-3 py-2 text-sm hover:border-[#002FA7] transition-colors">
                <span className="text-[#111827]"><b>{r.company_name}</b> — {r.note}</span>
                <span className="font-mono text-[#B45309] font-semibold">{dateFmt(r.date)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Pipeline board */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sales-pipeline">
        {LEAD_STAGES.map((stage) => {
          const inStage = leads.filter((l) => l.status === stage);
          return (
            <div key={stage} className="bg-[#F3F4F6] border border-[#E5E7EB] p-3 min-h-[120px]">
              <div className="flex items-center justify-between mb-2">
                <span className="overline text-[#4B5563]">{STAGE_LABEL[stage]}</span>
                <span className="font-mono text-xs text-[#9CA3AF]">{inStage.length}</span>
              </div>
              <div className="space-y-2">
                {inStage.map((l) => (
                  <Link key={l.id} to={`/leads/${l.id}`} data-testid={`sales-lead-${l.id}`}
                    className="block bg-white border border-[#E5E7EB] p-3 hover:border-[#002FA7] transition-colors">
                    <div className="font-semibold text-sm text-[#111827] leading-tight">{l.company_name}</div>
                    <div className="flex items-center gap-1 text-[11px] text-[#4B5563] mt-1"><MapPin size={11} weight="fill" /> {l.distance_miles} mi</div>
                    <div className="flex items-center gap-2 mt-2 text-[#002FA7]">
                      {l.phone && <Phone size={13} weight="fill" />}
                      {l.email && <EnvelopeSimple size={13} weight="fill" />}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* List view */}
      <h2 className="font-display font-bold text-xl text-[#111827] mt-10 mb-3">All assigned leads</h2>
      <div className="grid md:grid-cols-12 gap-3 mb-5 bg-white border border-[#E5E7EB] p-3">
        <div className="md:col-span-8 relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input data-testid="sales-lead-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search your leads…" className="w-full pl-9 pr-3 py-2 border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
        <select data-testid="sales-lead-category" value={cat} onChange={(e) => setCat(e.target.value)}
          className="md:col-span-4 border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="all">All business types</option>
          {LEAD_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="space-y-3" data-testid="sales-lead-list">
        {leads.length === 0 && (
          <div className="border border-dashed border-[#E5E7EB] py-16 text-center text-[#4B5563]">
            No leads assigned to you yet. Your GM will assign prospects here.
          </div>
        )}
        {filtered.map((l) => (
          <Link key={l.id} to={`/leads/${l.id}`} className="rb-card rb-card-hover flex items-center justify-between p-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display font-bold text-lg text-[#111827]">{l.company_name}</span>
                {l.source === "Front Desk" && <span className="inline-flex items-center gap-1 bg-[#CFFAFE] text-[#0E7490] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">Front Desk</span>}
                {(l.reminders || []).some((r) => !r.done) && <BellRinging size={14} weight="fill" color="#B45309" />}
              </div>
              <div className="text-sm text-[#4B5563]">{l.industry} · {l.city || l.hotel_name} · {l.phone || l.email || "no contact"}</div>
            </div>
            <StatusBadge status={l.status} />
          </Link>
        ))}
      </div>
      </>)}
    </div>
  );
}
